import React from 'react';
import { C, CT } from '../../constants/theme';
import { LTR_TEXT } from '../../i18n/I18nProvider.jsx';
import { recordTitle, labelFor } from '../../screens/Visit/Visit.shared.js';

// «تاریخچهٔ دندان ۳۶» — everything that ever happened to ONE tooth, in order.
//
// ── Why the two lists had to become one ─────────────────────────────────────
// A tooth carries two kinds of row, written by two different acts:
//
//   ○ a CHART EVENT    what is true of the tooth — «پوسیدگی، سطح O»
//   ● a TREATMENT      what was done to it — «عصب‌کشی مولر · جلسهٔ ۳ از ۳»
//
// The visit screen showed only the first. So «چه بلایی سر این دندان آمده؟» —
// one question — was answered by half the record, and the half that was missing
// is the one carrying the who, the when, the visit, the session and the
// material. A dentist had to open طرح درمان, find the line, and read the
// records list to learn that they themselves had root-canalled it last month.
//
// Merging them by time is the whole fix. They are one story and a dentist reads
// them as one; keeping two lists made them merge it in their head, in the wrong
// order, from two screens.
//
// ── Voided rows stay ────────────────────────────────────────────────────────
// Struck through, with the reason, both kinds. A record is corrected by
// superseding it, and a history that silently drops the correction is one
// nobody can audit — the same rule the chart has always followed for events.
//
// ── Read-only, on every surface ─────────────────────────────────────────────
// This renders; it never writes. Recording a treatment belongs to a visit and
// charting belongs to the tooth editor, so a list that offered either would be
// a third way to write the record.

/** ● treatment · ○ chart event, as one sorted list, newest first. */
export function mergeToothHistory(events = [], records = [], catalog = []) {
  const rows = [];
  for (const e of events || []) {
    rows.push({
      kind: 'event',
      id: `e-${e.id}`,
      at: Number(e.occurredAtMs || e.recordedAtMs) || 0,
      voidedAtMs: e.voidedAtMs || null,
      voidReason: e.voidReason || null,
      // Set when this event was written BY a treatment. Phase 3 stamps it
      // server-side; until then it is always absent and every event renders as
      // its own row, which is what happened before this existed.
      sourceRecordId: e.sourceRecordId || null,
      event: e,
    });
  }
  for (const r of records || []) {
    rows.push({
      kind: 'treatment',
      id: `r-${r.id}`,
      recordId: r.id,
      at: Number(r.performedAtMs || r.recordedAtMs) || 0,
      voidedAtMs: r.voidedAtMs || null,
      voidReason: r.voidReason || null,
      title: recordTitle(r, catalog),
      record: r,
    });
  }
  rows.sort((a, b) => b.at - a.at);

  // A chart event a treatment caused is NESTED under it rather than listed
  // beside it: on the same day, at the same second, «عصب‌کشی مولر» and
  // «وضعیت: عصب‌کشی‌شده» are one act described twice, and two sibling rows read
  // as two separate findings.
  // The parent is found in the WHOLE list, not in what has been emitted so far.
  // They are written in one transaction and routinely carry the same timestamp
  // to the second, so which of the two sorts first is a coin toss — and looking
  // only backwards meant the common case (identical stamps, event first) nested
  // nothing at all.
  const byRecord = new Map(
    rows.filter((r) => r.kind === 'treatment').map((r) => [r.recordId, r]),
  );
  const out = [];
  for (const row of rows) {
    if (row.kind === 'event' && row.sourceRecordId) {
      const parent = byRecord.get(row.sourceRecordId);
      if (parent) {
        (parent.caused ||= []).push(row);
        continue;
      }
    }
    // An event whose treatment is not in THIS list still shows, on its own: the
    // records may be narrowed to one visit while the events are not, and
    // dropping it would hide a chart change entirely.
    out.push(row);
  }
  return out;
}

const Line = ({ children, muted, struck, indented }) => (
  <div style={{
    display: 'flex', gap: 8, alignItems: 'baseline',
    fontSize: 12, lineHeight: 1.9,
    paddingInlineStart: indented ? 18 : 0,
    color: muted ? C.textMuted : C.textSecondary,
    textDecoration: struck ? 'line-through' : 'none',
  }}>
    {children}
  </div>
);

export default function ToothHistory({
  fdi, events = [], records = [], catalog = [],
  loading = false, error = false,
  t, formatDate,
  // «مشاهدهٔ ویزیت» — absent on a surface with nowhere to go (the patient
  // record's read-only chart), so no button is drawn that leads nowhere.
  onOpenVisit = null,
}) {
  const rows = React.useMemo(
    () => mergeToothHistory(events, records, catalog),
    [events, records, catalog],
  );

  if (loading) {
    return <span style={{ fontSize: 12, color: C.textMuted }}>{t('common.loading')}</span>;
  }
  if (error) {
    return (
      <span style={{ fontSize: 12, color: C.textMuted }}>
        {t('chart.history.unavailable')}
      </span>
    );
  }
  if (!rows.length) {
    return (
      <span style={{ fontSize: 12, color: C.textMuted }}>{t('chart.history.empty')}</span>
    );
  }

  const when = (at) => (at && formatDate ? formatDate(Number(at), 'numeric') : '—');

  // `labelFor` rather than a bare `t()`: the chart's vocabularies grow, and a
  // value with no string yet must render as itself rather than as the raw key
  // path — which is what the dentist would otherwise read in their own history.
  const eventLine = (e) => [
    e.aspect === 'surface' && e.surface ? `${t('chart.surfaces.title')} ${e.surface}` : null,
    labelFor(t, 'tooth.value', e.value) || null,
    labelFor(t, 'tooth.material', e.material) || null,
    e.note || null,
  ].filter(Boolean).join(' · ');

  const treatmentLine = (r) => [
    r.surfaces || null,
    labelFor(t, 'tooth.material', r.material) || null,
    Number(r.sessionNo) > 1 ? t('chart.history.session', { n: r.sessionNo }) : null,
    r.note || null,
  ].filter(Boolean).join(' · ');

  return (
    <div data-testid={`tooth-history-${fdi}`}>
      {rows.map((row) => (
        <React.Fragment key={row.id}>
          <Line muted={!!row.voidedAtMs} struck={!!row.voidedAtMs}>
            <span style={{ flexShrink: 0 }} {...LTR_TEXT}>{when(row.at)}</span>
            <span style={{
              flexShrink: 0, fontSize: 10.5, fontWeight: 700,
              color: row.kind === 'treatment' ? CT.accentInk : C.textMuted,
            }}>
              {row.kind === 'treatment'
                ? `● ${t('chart.history.treatment')}`
                : `○ ${t('chart.history.event')}`}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {row.kind === 'treatment'
                ? [row.title, treatmentLine(row.record)].filter(Boolean).join(' · ')
                : eventLine(row.event)}
              {row.voidedAtMs && (
                <span style={{ color: C.textMuted }}>
                  {' '}{t('chart.history.voided', { reason: row.voidReason || '—' })}
                </span>
              )}
            </span>
            {row.kind === 'treatment' && onOpenVisit && row.record?.appointmentId && (
              <button
                type="button"
                onClick={() => onOpenVisit(row.record.appointmentId)}
                style={{
                  flexShrink: 0, background: 'none', border: 'none', padding: 0,
                  font: 'inherit', fontSize: 11, color: CT.accentInk, cursor: 'pointer',
                }}
              >
                {t('visit.record.openVisit')}
              </button>
            )}
          </Line>

          {(row.caused || []).map((child) => (
            <Line key={child.id} indented muted struck={!!child.voidedAtMs}>
              <span style={{ flexShrink: 0, fontSize: 10.5 }}>↳</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                {eventLine(child.event)}
                <span style={{ color: C.textMuted }}> — {t('chart.history.causedBy')}</span>
              </span>
            </Line>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}
